import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {isEmpty} from 'lodash';
import ReactSignatureCanvas from 'react-signature-canvas';
import api from 'api';
import SessionStorage from '../../utils/storage/SessionStorage';
import useMixPanel from 'hooks/useMixPanel';
import {GUEST_APP_EVENTS_NAME, STORAGE_KEYS} from 'utils/constants';
import {useReservation} from 'context/reservation';
import {useErrorModal} from 'utils/hooks';
import {downloadFromLink} from '../../utils/common';
import signIcon from 'assets/sign-icon.svg';
import Modal from 'components/Modal';
import Button from 'components/acommon/Button';
import Icon from 'components/acommon/Icon';
import SignatureCanvas from 'components/acommon/SignatureCanvas';
import Link from 'components/acommon/Link';
import styles from './SignBlock.module.sass';
import {PleaseSignModal, SuperhogTerms, WrapperWithButton} from './styled';
import {fakeSignature} from './index';
import Checkbox from 'components/Checkbox';
import Tooltip from 'components/Tooltip';
import {TooltipTrigger} from 'components/AddPersonalDataForm/styled';
import {getHasSuperHog} from 'utils/reservation';

export enum CHECKBOXES {
  terms = 'terms',
  contract = 'contract',
}

// type CheckboxesType = {
//   [key: string]: boolean;
// };
//
// type LocationTypes = {
//   formData: any;
//   number_of_guests?: number;
//   children?: number;
//   ocrWasUsed?: boolean;
//   backSideScan: string;
//   frontSideScan?: string;
//   biomatch?: {
//     biomatch_passed: boolean;
//     biomatch_doc: string;
//     biomatch_selfie: string;
//   };
// };

type ContractType = {
  file: string;
};

export type SignBlockRef = {
  reset: () => void;
  set: (base64: string) => void;
  isEnabled: boolean;
  canvas?: HTMLCanvasElement;
};

export type SignBlockProps = {
  onSubmit: (signature: string) => void;
  isSignModalOpen: boolean;
  setIsSignModalOpen: (value: boolean) => void;
  showSignArea?: boolean;
};

const SignBlock = React.forwardRef<SignBlockRef, SignBlockProps>(
  (
    {onSubmit, isSignModalOpen, setIsSignModalOpen, showSignArea}: SignBlockProps,
    ref,
  ) => {
    const {t} = useTranslation();
    const signatureRef = React.useRef<ReactSignatureCanvas>(null);
    const {ErrorModal: ErrorModalContract, displayError: displayContractError} =
      useErrorModal();
    const {data: reservation} = useReservation();
    const isContractEnabled = reservation?.housing?.is_contract_enabled;

    const [isLoadingContracts, setIsLoadingContracts] = React.useState(false);
    const [isContractsError, setIsContractsError] = React.useState(false);
    const [signature, setSignature] = React.useState<string>('');
    const [isSignatureEnabled, setIsSignatureEnabled] = React.useState(false);
    const [contracts, setContracts] = React.useState<Array<ContractType>>([]);
    const {IdGuestAppEvents} = useMixPanel();
    const [hasSuperhog, setHasSuperHog] = React.useState(false);
    const [superHogTerms, setSuperHogTerms] = React.useState(true);
    const [issuperHogTermsModalOpen, setIsSuperHogTermsModalOpen] = React.useState(false);
    const [propertyHasSuperHog, setPropertyHasSuperHog] = React.useState(false);

    const fetchContracts = React.useCallback(async () => {
      const reservationId = reservation?.id;
      if (!reservationId) {
        return;
      }

      setIsContractsError(false);
      setIsLoadingContracts(true);
      const {data, error} = await api.documents.getContracts(
        `housing_sample_id=${reservation?.housing?.id}`,
      );

      if (data) {
        setContracts(data);
      }
      if (error) {
        setIsContractsError(true);
      }
      setIsLoadingContracts(false);
    }, [reservation]);

    React.useEffect(() => {
      if (isContractEnabled) {
        fetchContracts();
      }
    }, [fetchContracts, isContractEnabled]);

    React.useEffect(
      function preloadPersistedLocationSignature() {
        const storageSignature = SessionStorage.get<string>(
          STORAGE_KEYS.personalSignatureFormData,
        );
        if (storageSignature && reservation?.id) {
          signatureRef.current?.fromDataURL(storageSignature);
          setIsSignatureEnabled(true);
          setSignature(storageSignature);
        }
      },
      [reservation?.id, signatureRef],
    );

    React.useEffect(
      function getPropertyHasSuperHog() {
        if (getHasSuperHog(reservation)) {
          setPropertyHasSuperHog(true);
        }
      },
      [reservation],
    );

    React.useEffect(
      function getIsSuperHogReservation() {
        if (!reservation) {
          return;
        }
        const hasSuperHog =
          reservation?.housing?.has_host_protection_insurance &&
          reservation?.housing?.host_protection_insurance_provider === 'Superhog';
        setHasSuperHog(hasSuperHog);
      },
      [reservation],
    );

    const clearSignature = () => {
      signatureRef.current?.clear();
      setSignature('');
    };

    const resetSignature = () => {
      clearSignature();
      setIsSignatureEnabled(false);
    };

    const putSignature = (base64: string) => {
      setIsSignatureEnabled(true);
      setSignature(base64);
    };

    React.useImperativeHandle(ref, () => ({
      reset: resetSignature,
      set: putSignature,
      canvas: signatureRef.current?.getCanvas(),
      isEnabled: isSignatureEnabled,
    }));

    const saveSignature = () => {
      const signature = signatureRef.current?.getCanvas()?.toDataURL();
      if (signature) setSignature(signature);
    };

    const handleSignatureEnable = () => {
      setIsSignatureEnabled(true);
    };

    const handleSignModalClose = () => {
      setIsSignModalOpen(false);
      handleSignatureEnable();
    };

    const handleSuperHogTermsModalClose = () => {
      setIsSuperHogTermsModalOpen(false);
    };

    const viewContract = () => {
      IdGuestAppEvents(GUEST_APP_EVENTS_NAME.contractDownloaded);
      const contract = contracts[0];
      if (isContractsError || !contract?.file) {
        displayContractError();
        return;
      }

      downloadFromLink(contract?.file);
    };

    const handleSubmit = async () => {
      const showSignModal = isSignatureEnabled && isEmpty(signature) && showSignArea;

      if (!showSignModal && !showSignArea) {
        onSubmit(fakeSignature);
      }

      if (isEmpty(signature) && showSignArea) {
        setIsSignModalOpen(true);
      }

      if (propertyHasSuperHog && !superHogTerms) {
        setIsSuperHogTermsModalOpen(true);
        return;
      }

      if (!showSignModal && !isEmpty(signature)) {
        onSubmit(signature);
      }
    };

    if (isEmpty(reservation)) {
      return null;
    }

    return (
      <>
        <ErrorModalContract
          title={t('contract_not_available')}
          message={t('contract_not_available_description')}
        />
        <Modal open={isSignModalOpen} onClose={handleSignModalClose}>
          <PleaseSignModal>
            <img src={signIcon} alt="" width="84" height="84" />
            <div className="title">{t('you_have_to_sign')}</div>
            <div className="text">
              {isContractEnabled ? (
                <Trans i18nKey="please_sign_and_accept_terms_and_contract">
                  Please <b>sign</b> inside the box. You can use your finger.
                </Trans>
              ) : (
                <Trans i18nKey="please_sign_and_accept_terms">
                  Please <b>sign</b> inside the box. You can use your finger.
                </Trans>
              )}
            </div>
            <Button label={t('sign_now')} onClick={handleSignModalClose} />
          </PleaseSignModal>
        </Modal>
        <Modal open={issuperHogTermsModalOpen} onClose={handleSuperHogTermsModalClose}>
          <PleaseSignModal>
            <img src={signIcon} alt="" width="84" height="84" />
            <div className="title">{t('accept_superhog_terms')}</div>
            <div className="text">
              <Trans i18nKey="accept_superhog_terms_description">
                In order to continue you must accept the{' '}
                <b>Superhog data verification agreement.</b>
              </Trans>
            </div>
            <Button label={t('ok')} onClick={handleSuperHogTermsModalClose} />
          </PleaseSignModal>
        </Modal>
        <WrapperWithButton>
          {
            <div className={styles.signWrapper}>
              {showSignArea && (
                <>
                  {isLoadingContracts && (
                    <div className={styles.contractWrapper}>{t('loading_contract')}</div>
                  )}
                  {isContractEnabled && !isLoadingContracts && (
                    <div
                      className={styles.contractWrapper}
                      data-testid="contract-btn"
                      onClick={viewContract}
                    >
                      <Icon name="search" className={styles.contractIcon} />
                      {t('view_contract')}
                    </div>
                  )}
                  <SignatureCanvas
                    ref={signatureRef}
                    onClear={clearSignature}
                    onEnable={handleSignatureEnable}
                    enabled={isSignatureEnabled}
                    hasSignature={Boolean(signature)}
                    onEnd={saveSignature}
                  />
                </>
              )}
              <div className={styles.acceptWarning}>
                {t('by_clicking_next_you_accept_the')}{' '}
                {isContractEnabled && !isLoadingContracts && (
                  <span
                    className={styles.linkInText}
                    data-testid="contract-btn"
                    onClick={viewContract}
                  >
                    {t('contract')}
                  </span>
                )}
                {isContractEnabled &&
                  !isLoadingContracts &&
                  ` ${t('and_you_accept_our')} `}
                <Link
                  target="_blank"
                  rel="noreferrer"
                  href={t('terms_and_conditions_link')}
                  action
                >
                  {t('terms_and_conditions')}
                </Link>
                {hasSuperhog && (
                  <SuperhogTerms>
                    <Tooltip position="top right" label={t('superhog_info_tooltip')}>
                      <TooltipTrigger>
                        <Checkbox
                          onChange={() => setSuperHogTerms(!superHogTerms)}
                          name="byEmail"
                          checked={superHogTerms}
                          label={t('accept_superhog_data_verification')}
                          linkLabel={t('accept_superhog_data_verification_title')}
                          linkHref={t('terms_and_conditions_link_super_hog')}
                        />
                      </TooltipTrigger>
                    </Tooltip>
                  </SuperhogTerms>
                )}
              </div>
              <div>
                <div className={styles.bookingDetailsSubmitMobile}>
                  <div className={styles.progress}>1/5</div>
                  <Button
                    data-testid="submit-btn"
                    onClick={handleSubmit}
                    label={t('next')}
                    type="button"
                    rightIcon="arrow-next"
                    className={styles.submitButton}
                  />
                </div>
              </div>
            </div>
          }
        </WrapperWithButton>
      </>
    );
  },
);

export {SignBlock};
